import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  artistInitialValues,
  ArtistTabs,
} from 'components/organisms/forms/artist/ArtistForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiUserPlus, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetArtist, useUpdateArtist } from 'services/artists';
import {
  useAddArtistImage,
  useDeleteArtistImage,
} from 'services/artists/images';
import {
  useChangeProfilePicture,
  useCreateProfilePicture,
  useDeleteProfilePicture,
} from 'services/artists/profilePicture';
import { IArtistMutationForm } from 'services/artists/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const EditArtist = () => {
  const artistId = useParamId('artist');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const backPathname = `/artist/${artistId}/${getLastPathname(pathname)}`;
  const { data: artist, isLoading } = useGetArtist(artistId);
  const updateArtist = useUpdateArtist();
  const changeProfilePicture = useChangeProfilePicture();
  const createProfilePicture = useCreateProfilePicture();
  const deleteProfilePicture = useDeleteProfilePicture(artistId);

  const addImage = useAddArtistImage();
  const deleteImage = useDeleteArtistImage(artistId);

  const mutateOptions = useMutateOptions<IArtistMutationForm>();

  const onUpdateArtist = async (
    values: IArtistMutationForm,
    helpers: FormikHelpers<IArtistMutationForm>,
  ) => {
    const updatedArtist = middleware.artist.formToQuery(values);

    let artistVersion = values.version;

    if (values.file) {
      const shouldUpdateExistingPicture =
        artist?.hasManualyUploadedPicture === true &&
        !!artist?.profilePictureLink?.length;

      shouldUpdateExistingPicture
        ? await changeProfilePicture.mutateAsync(
            {
              file: values.file,
              id: artistId,
              version: values.version,
            },
            {
              onSuccess: a => {
                artistVersion = a.version;
              },
              onError: error => onErrorShowAllErrorsToast(error),
            },
          )
        : await createProfilePicture.mutateAsync(
            {
              id: artistId,
              file: values.file,
              version: values.version,
            },
            {
              onSuccess: a => {
                artistVersion = a.version;
              },
              onError: error => onErrorShowAllErrorsToast(error),
            },
          );
    }

    if (
      artist?.hasManualyUploadedPicture === true &&
      !!artist?.profilePictureLink?.length &&
      !values.profilePictureLink?.length &&
      !values.file
    ) {
      await deleteProfilePicture.mutateAsync(undefined, {
        onSuccess: a => {
          artistVersion = a.version ?? values.version;
        },
        onError: error => onErrorShowAllErrorsToast(error),
      });
    }

    await updateArtist.mutateAsync(
      { artistId: artistId, ...updatedArtist, version: artistVersion },
      {
        ...mutateOptions(helpers),
        onSuccess: () =>
          !(
            values.gallery.deleteImages.length > 0 ||
            values.gallery.pendingImages.length > 0
          ) && navigate(backPathname),
      },
    );

    if (values.gallery.deleteImages.length > 0) {
      await Promise.all(
        values.gallery.deleteImages.map(async imageId => {
          await deleteImage.mutateAsync(
            { imageId },
            {
              onSuccess: () =>
                !(values.gallery.pendingImages.length > 0) &&
                navigate(backPathname),
              onError: error => {
                onErrorShowAllErrorsToast(error as Error);
              },
            },
          );
        }),
      );
    }

    if (values.gallery.pendingImages.length > 0) {
      await Promise.all(
        values.gallery.pendingImages.map(async file => {
          await addImage.mutateAsync(
            {
              file,
              id: artistId,
              isMain: true,
            },
            {
              onSuccess: () => navigate(backPathname),
              onError: error => {
                onErrorShowAllErrorsToast(error as Error);
              },
            },
          );
        }),
      );
    }
  };

  return (
    <Form.Artist
      i18nextPrefix='artist'
      initialValues={
        artist ? middleware.artist.queryToForm(artist) : artistInitialValues
      }
      onSubmit={onUpdateArtist}
      customBackItem={
        <Button onlyIcon onClick={() => navigate(backPathname)}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.save')} />
          <Button.Icon icon={<PiUserPlus />} />
        </Button>
      }
      status={{ isLoading }}
      disableTabs={[
        ArtistTabs.bio,
        ArtistTabs.actions,
        ArtistTabs.products,
        ArtistTabs.tracks,
        ArtistTabs.duplicates,
      ]}
    />
  );
};

export default EditArtist;
